import { ethers } from "ethers";
import IVerseHub from "./contracts/IVerseHub.json";
import VerseLand from "./contracts/VerseLand.json";

const fallbackProvider = new ethers.providers.JsonRpcProvider({
  url: process.env.REACT_APP_ETH_RPC_URL,
});

const updateProvider = (provider) => {
  if (!provider) window._ethers = null;
  else window._ethers = new ethers.providers.Web3Provider(provider);
};

const _getEthers = () => {
  return window._ethers ?? fallbackProvider;
};

const _getSigner = () => {
  const ether = _getEthers();
  return ether === fallbackProvider ? ether : ether.getSigner();
};

const fetchBalance = async (address) => {
  const ether = _getEthers();

  const balance = await ether.getBalance(address);
  return ethers.utils.formatEther(balance);
};

const _getHubContract = () => {
  const signer = _getSigner();

  const address = process.env.REACT_APP_HUB_CONTRACT_ADDRESS;
  return new ethers.Contract(address, IVerseHub.abi, signer);
};

const _getLandContract = () => {
  const signer = _getSigner();

  const address = process.env.REACT_APP_LAND_CONTRACT_ADDRESS;
  return new ethers.Contract(address, VerseLand.abi, signer);
};

const getLand = async (id) => {
  const verseHub = _getHubContract();
  return verseHub.getLand(id);
};

const batchGetLands = async (ids) => {
  const verseHub = _getHubContract();
  return verseHub.batchGetLands(ids.map((id) => id.toString()));
};

const getLandPrice = async (id) => {
  const verseHub = _getHubContract();
  const price = await verseHub.landPrice(id);
  return ethers.utils.formatEther(price);
};

const getBuyLandEstimateGasPrice = async (id, price) => {
  const ether = _getEthers();
  const verseHub = _getHubContract();

  const recipient = await _getSigner().getAddress();
  const gasPrice = await ether.getGasPrice();
  const gas = await verseHub.estimateGas.buyLand(recipient, id, {
    value: ethers.utils.parseEther(price),
  });
  const gasFees = gas.mul(gasPrice);

  return ethers.utils.formatEther(gasFees);
};

const buyLand = async (id, price) => {
  const recipient = await _getSigner().getAddress();
  const verseHub = _getHubContract();
  return await verseHub.buyLand(recipient, id, {
    value: ethers.utils.parseEther(price),
  });
};

const getOwnedTokenIds = async (address) => {
  const verseLand = _getLandContract();
  const tokenIds = await verseLand.tokensOfOwner(address);
  return tokenIds.map((id) => id.toString());
};

const getLandOwner = async (tokenId) => {
  const verseLand = _getLandContract();
  return await verseLand.ownerOf(tokenId);
};

const getHubName = async () => {
  const verseHub = _getHubContract();
  return await verseHub.name();
};

const getZones = async (ids) => {
  const verseHub = _getHubContract();
  const storedZones = await verseHub.batchGetZones(ids.map((id) => id.toString()));

  var zones = [];
  for (var i = 0; i < ids.length; i++) {
    const isAvailable = storedZones[i].basePrice.isZero();
    zones.push({ id: ids[i], available: !isAvailable });
  }
  return zones;
};

const waitForTransaction = async (tx) => {
  const ether = _getEthers();

  return await ether.waitForTransaction(tx);
};

const signMessage = async (message) => {
  const ether = _getEthers();
  const signer = ether.getSigner();

  return await signer.signMessage(message);
};

export {
  updateProvider,
  fetchBalance,
  getLand,
  batchGetLands,
  getLandPrice,
  getBuyLandEstimateGasPrice,
  buyLand,
  getOwnedTokenIds,
  getLandOwner,
  getHubName,
  getZones,
  waitForTransaction,
  signMessage,
};
